import { type ReactNode } from 'react';

import {
  Controller,
  type FieldPath,
  type FieldValues,
  type PathValue,
} from 'react-hook-form';

import { FormFieldError } from '@/components/Form/FormFieldError';
import { FormFieldHelper } from '@/components/Form/FormFieldHelper';
import { FormFieldItem } from '@/components/Form/FormFieldItem';
import { FormFieldLabel } from '@/components/Form/FormFieldLabel';
import { Select, type SelectProps } from '@/components/Select';

import { type FieldCommonProps } from '../FormField.types';

export type FieldSelectProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  type: 'select';
  label?: ReactNode;
  helper?: ReactNode;
  options: Readonly<
    Array<
      Readonly<{
        label: string;
        value: PathValue<TFieldValues, TName>;
      }>
    >
  >;
} & Pick<
  SelectProps,
  | 'size'
  | 'placeholder'
  | 'autoFocus'
  | 'onMenuClose'
  | 'menuPlacement'
  | 'formatOptionLabel'
> &
  FieldCommonProps<TFieldValues, TName>;

export const FieldSelect = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
  props: FieldSelectProps<TFieldValues, TName>
) => {
  return (
    <Controller
      {...props}
      render={({ field, fieldState }) => {
        const { value, onChange, ...fieldProps } = field;
        const selectValue =
          props.options?.find((option) => option.value === value) ?? undefined;
        return (
          <FormFieldItem>
            {!!props.label && <FormFieldLabel>{props.label}</FormFieldLabel>}
            <Select
              type="select"
              size={props.size}
              options={props.options}
              placeholder={props.placeholder}
              autoFocus={props.autoFocus}
              value={selectValue}
              isDisabled={props.isDisabled}
              onMenuClose={props.onMenuClose}
              menuPlacement={props.menuPlacement}
              formatOptionLabel={props.formatOptionLabel}
              isInvalid={!!fieldState.error} // TODO: check how input pass this without fieldState
              onChange={(option) => onChange(option?.value)}
              {...fieldProps}
            />

            {!!props.helper && (
              <FormFieldHelper>{props.helper}</FormFieldHelper>
            )}
            <FormFieldError />
          </FormFieldItem>
        );
      }}
    />
  );
};
